'use client';

import type { ReactNode } from 'react';

import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

/** 판 안의 한 줄 — 이름 · (딸린 말) · 스위치 */
export type HubToggleItem = {
  key: string;
  label: string;
  /** 이름 아래 흐린 한 줄 */
  hint?: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** 처음 값 — 주면 `resetToggleItems` 가 이 값으로 되돌린다 */
  defaultOn?: boolean;
};

/**
 * 칸마다 처음 값으로 되돌린다 — 판에서 하나씩 켜고 끈 것과 같다(이미 처음 값인 칸은 부르지 않는다).
 * @param items 처음 값(`defaultOn`)이 없는 칸은 건너뛴다
 */
export function resetToggleItems(items: readonly HubToggleItem[]): void {
  items.forEach((item) => {
    if (item.defaultOn !== undefined && item.checked !== item.defaultOn) item.onChange(item.defaultOn);
  });
}

/**
 * **켜고 끄는 칸 목록** — 조건 팝오버(`HubConditionPopover`) 안에 선다(「수집 기준」).
 *
 * ```
 *   AI 뷰티 필터                [●]
 *   브랜드 인식 기준             [ ]
 * ```
 *
 * 🔑 값과 바꾸는 함수는 화면의 것이다 — 이 부품은 줄 모양만 쥔다.
 * ⚠️ 스위치를 칩으로 바꾸지 마라 — 켜고 끄는 것과 «고르는 것»(칩)은 다르게 읽혀야 한다.
 */
export function HubToggleList({ items }: { items: readonly HubToggleItem[] }) {
  return (
    <Stack spacing={1}>
      {items.map((item) => (
        <Stack key={item.key} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="uiBody">{item.label}</Typography>
            {item.hint && <Typography variant="uiMeta" color="text.secondary">{item.hint}</Typography>}
          </Stack>
          <Switch
            size="small"
            checked={item.checked}
            onChange={(e) => item.onChange(e.target.checked)}
            slotProps={{ input: { 'aria-label': item.label } }}
          />
        </Stack>
      ))}
    </Stack>
  );
}
